import { endOfMonth, getDaysInMonth, set, startOfMonth } from 'date-fns';
import React from 'react';

import { ADVICE_STATUS, TYPES } from 'constants/ChecklistConstants';
import { API_DATE_FORMAT } from 'constants/Global';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';

import type { MayBe } from 'modules/global/typing/common/common';
import type { Label } from 'modules/global/typing/common/Label';

import { tryParse } from './date';
import { isObject } from './ObjectUtils';

/** Any object indexable by an arbitrary string key, as read by the helpers below. */
type UnknownRecord = Record<string, unknown>;

export const humanize = (str: string) =>
  str
    ?.replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, (match: string) => match.toUpperCase()) ?? '';

/** Zero-pads every number group so `1.9.0` sorts before `1.10.0` under a plain string compare. */
const padVersionNumbers = (version: string) => version.replace(/\d+/g, (n: string) => `${+n + 100000}`);

export const sortByVersion =
  (field: string) =>
    (a: UnknownRecord, b: UnknownRecord): number => {
      const left = a[field];
      const right = b[field];

      if (typeof left !== 'string' || typeof right !== 'string') {
        return 0;
      }

      return padVersionNumbers(left).localeCompare(padVersionNumbers(right), undefined, { numeric: true });
    };

export const indexByProp = <T extends UnknownRecord>(
  field: string,
  data: MayBe<T[]> | null,
  initialValue: Record<string, T> = {}
): Record<string, T> =>
    data?.reduce<Record<string, T>>((acc, item) => {
      if (item?.[field] !== undefined) {
        return {
          ...acc,
          [String(item[field])]: item
        };
      }
      return acc;
    }, initialValue) ?? {};

export const camelCase = (str: string) =>
  str
    .replace(/_|\W/g, ' ')
    .toLowerCase()
    .replace(/\b(\W.)/g, (word: string) => word.trim().toUpperCase());

export const isUndefined = (value: unknown) => typeof value === 'undefined';

export const isEmpty = (item: MayBe<object | string> | null) => !item || Object.keys(item).length === 0;

export const filter = <T, >(
  predicate: (item: T, key: string, index: number) => unknown,
  collection: MayBe<Record<string, T>>
): Record<string, T> => {
  if (Array.isArray(collection)) {
    throw new Error('Please use Array.filter function');
  }

  return Object.entries(collection ?? {}).reduce<Record<string, T>>((acc, [key, item], index) => {
    if (predicate(item, key, index)) {
      acc[key] = item;
    }
    return acc;
  }, {});
};

/**
 * @param {string} str
 * @returns str with lowercased first letter, `''` if falsy value.
 */
export const lowercaseFirstLetter = (str: string) =>
  (!str || str.length === 0 ? '' : str.replace(/^./, (match: string) => match.toLowerCase()));

/**
 * Make a string's first character uppercase
 * @param {string} str
 * @return {string}
 */
export const ucFirst = (str: string) =>
  (Boolean(str) && str.length >= 2 ? `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}` : str);

/**
 * Resolve a {@link Label} to a single string: the requested language, then the dealer's language,
 * then `en_us`, then any translation that actually carries text. A label whose every entry is
 * empty still resolves to that empty string rather than `undefined`, so callers binding the
 * result straight to a controlled input keep a defined value.
 */
export const getLabelTranslation = (
  translation: MayBe<Label>,
  language: string,
  dealerLanguage: string = DEFAULT_LANGUAGE
): MayBe<string> => {
  const translations = Object.values(translation ?? {});

  return translation?.[language] ??
    translation?.[dealerLanguage] ??
    translation?.[DEFAULT_LANGUAGE] ??
    translations.find(Boolean) ??
    translations[0];
};

/**
 * @param {string|Date} value
 * @returns {Date|undefined}
 */
export const dateFormatter = (value: MayBe<string | Date>) => tryParse(value, { formatOrigin: API_DATE_FORMAT });

/**
 * @param {string|Date} value
 * @returns {string|undefined}
 */
export const dateParser = (value: MayBe<string | Date>) => tryParse(value, { formatDestination: API_DATE_FORMAT });

/**
 * @param {string} value
 * @returns {string}
 */
export const periodParser = (value: string) => (value ? `P${value}M` : undefined);

/**
 * @param {string} value
 * @returns {number|null}
 */
export const periodFormatter = (value: string) => {
  const regex = /^P([0-9]+)M$/;
  const matches = regex.exec(value);
  if (matches) {
    return parseInt(matches[1], 10);
  }
  return null;
};

export const monthlyRevenueParser = (revenue: unknown): MayBe<number> =>
  (typeof revenue === 'number' ? revenue : undefined);

export const groupBy = <T extends UnknownRecord>(data: T[], field: string): Record<string, T[]> =>
  data.reduce<Record<string, T[]>>(
    (acc, item) => {
      const key = String(item[field]);
      return {
        ...acc,
        [key]: [...(acc[key] ?? []), item]
      };
    },
    {}
  );

export const bold = (chunks: React.ReactNode) => <b>{chunks}</b>;

/**
 * @param {string} type
 * @param {string} status
 * @returns {string}
 */
export const formatStatus = (type: string, status: string) => {
  if (type === TYPES.DONE) {
    return 'PREAPPROVED';
  }

  if (status === ADVICE_STATUS.PAID || status === ADVICE_STATUS.DEPOSIT_PAID) {
    return ADVICE_STATUS.ACCEPTED;
  }
  return status;
};

/**
 * @param {string} input
 */
export const htmlDecode = (input: string) => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent;
};

export function sortObjectByKeys<T extends UnknownRecord>(object: T, deep: boolean = false): T {
  return Object.keys(object)
    .sort()
    .reduce<UnknownRecord>((acc, key) => {
      const value = object[key];
      return {
        ...acc,
        [key]: deep && isObject(value) ? sortObjectByKeys(value as UnknownRecord) : value
      };
    }, {}) as T;
}

export const getYearMonth = (date: string) => {
  // TODO: here we don't verify the format, we assume it's always correct
  const year = parseInt(date.match(/\d{4}/g)![0], 10);
  const month = parseInt(date.replace(year.toString(), '').match(/\d{2}/g)![0], 10) - 1;
  return { year, month };
};

/**
 * @param {string} from
 * @param {string} to
 * @returns {Array<Date>}
 */
export const getRangeFromStringWithoutDay = (from: string, to: string) => [
  from && startOfMonth(set(new Date(), getYearMonth(from))).toISOString(),
  to && endOfMonth(set(new Date(), getYearMonth(to))).toISOString()
];

/**
 * Check if the value is a string
 * @param value {any}
 * @returns {boolean}
 */
export const isString = (value: unknown) => typeof value === 'string' || value instanceof String;

/**
 * This function ensures that a given value falls within a specified range.
 * If the value is less than the minimum, it returns the minimum.
 * If the value is more than the maximum, it returns the maximum.
 * Otherwise, it returns the value itself.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const roundToRange = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * @param {number} month
 * @param {number} year
 * @returns {number}
 */
export const getDayInMonth = (month: number, year: number) => getDaysInMonth(new Date(year, month - 1));

/**
 * @param {string} str
 * @returns {string}
 */
export const toPascalCase = (str: string) =>
  str
    .toLowerCase()
    .replace(/[_. -]([a-z])/g, (_: string, c: string) => c.toUpperCase())
    .replace(/^\w/, (c: string) => c.toUpperCase());
